import { useState, useEffect, useRef, useCallback } from "react";
import { MessageSquare, X, Send, Paperclip, ChevronLeft, Plus, Download, FileText, Users, Hash } from "lucide-react";
import axios from "axios";
import { API, useAuth } from "../App";

const ax = () => { const t = localStorage.getItem("token"); return { headers: t ? { Authorization: `Bearer ${t}` } : {} }; };

const ChatBubble = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [groups, setGroups] = useState([]);
  const [currentGroup, setCurrentGroup] = useState(null);
  const [messages, setMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatFile, setChatFile] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNewGroup, setShowNewGroup] = useState(false);
  const [chatUsers, setChatUsers] = useState([]);
  const [newGroupName, setNewGroupName] = useState("");
  const [newGroupMembers, setNewGroupMembers] = useState([]);
  const [internalNotifications, setInternalNotifications] = useState([]);
  const msgEndRef = useRef(null);
  const pollRef = useRef(null);
  const lastMsgCountRef = useRef({});

  const isAdmin = ["super_admin", "admin_empresa", "gerente_rh", "supervisor"].includes(user?.role);

  // Fetch groups
  const fetchGroups = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/chat/groups`, ax());
      const grps = res.data || [];
      setGroups(grps);
      const notificationsRes = await axios.get(`${API}/internal-notifications`, ax()).catch(() => ({ data: [] }));
      setInternalNotifications(notificationsRes.data || []);
      // Calculate unread
      let unread = (notificationsRes.data || []).filter(item => !item.read).length;
      grps.forEach(g => {
        const lastKnown = lastMsgCountRef.current[g.group_id] || 0;
        if (g.last_message_at && !open) {
          const msgTime = new Date(g.last_message_at).getTime();
          const now = Date.now();
          if (msgTime > now - 60000 && lastKnown < msgTime) unread++;
        }
      });
      if (!open) setUnreadCount(prev => Math.max(prev, unread));
    } catch {}
  }, [open]);

  // Poll groups when closed, messages when open
  useEffect(() => {
    if (!user) return;
    fetchGroups();
    const interval = setInterval(fetchGroups, 10000);
    return () => clearInterval(interval);
  }, [user, fetchGroups]);

  // Fetch messages when group selected
  useEffect(() => {
    if (!currentGroup || !open) {
      if (pollRef.current) clearInterval(pollRef.current);
      return;
    }
    const loadMsgs = async () => {
      try {
        const res = await axios.get(`${API}/chat/groups/${currentGroup.group_id}/messages`, ax());
        setMessages(res.data || []);
        lastMsgCountRef.current[currentGroup.group_id] = Date.now();
      } catch {}
    };
    loadMsgs();
    pollRef.current = setInterval(loadMsgs, 4000);
    return () => clearInterval(pollRef.current);
  }, [currentGroup, open]);

  useEffect(() => { msgEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const handleOpen = () => { setOpen(true); setUnreadCount(0); };

  const markNotificationRead = async (notificationId) => {
    try {
      await axios.put(`${API}/internal-notifications/${notificationId}/read`, {}, ax());
      fetchGroups();
    } catch {}
  };

  const handleSend = async () => {
    if (!chatInput.trim() && !chatFile) return;
    if (!currentGroup) return;
    try {
      let payload = { text: chatInput };
      if (chatFile) {
        const reader = new FileReader();
        const fileData = await new Promise((resolve) => {
          reader.onload = (e) => resolve(e.target.result.split(",")[1]);
          reader.readAsDataURL(chatFile);
        });
        payload.file_name = chatFile.name;
        payload.file_data = fileData;
        payload.file_type = chatFile.type;
      }
      await axios.post(`${API}/chat/groups/${currentGroup.group_id}/messages`, payload, ax());
      setChatInput(""); setChatFile(null);
      const res = await axios.get(`${API}/chat/groups/${currentGroup.group_id}/messages`, ax());
      setMessages(res.data || []);
    } catch {}
  };

  const handleDownload = async (msgId, fileName) => {
    try {
      const res = await axios.get(`${API}/chat/messages/${msgId}/file`, { ...ax(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = fileName; a.click();
      const r2 = await axios.get(`${API}/chat/groups/${currentGroup.group_id}/messages`, ax());
      setMessages(r2.data || []);
    } catch {}
  };

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) return;
    try {
      await axios.post(`${API}/chat/groups`, { name: newGroupName, members: newGroupMembers }, ax());
      setNewGroupName(""); setNewGroupMembers([]); setShowNewGroup(false);
      fetchGroups();
    } catch {}
  };

  const loadUsers = async () => {
    try { const r = await axios.get(`${API}/chat/users`, ax()); setChatUsers(r.data || []); } catch {}
  };

  if (!user) return null;

  // Don't show on landing, login, register, mobile
  const path = window.location.pathname;
  if (["/" , "/login", "/register", "/mobile"].includes(path) || path.startsWith("/assessment")) return null;

  return (
    <>
      {/* FLOATING BUBBLE */}
      {!open && (
        <button onClick={handleOpen}
          className="fixed bottom-6 left-6 z-50 w-14 h-14 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 shadow-lg shadow-emerald-500/30 flex items-center justify-center text-white hover:scale-110 transition-all hover:shadow-xl hover:shadow-emerald-500/40 group"
          data-testid="chat-bubble-btn">
          <MessageSquare className="w-6 h-6 group-hover:scale-110 transition-transform" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center animate-pulse">{unreadCount}</span>
          )}
        </button>
      )}

      {/* CHAT PANEL */}
      {open && (
        <div className="fixed bottom-6 left-6 z-50 w-[380px] h-[520px] rounded-2xl bg-white border border-slate-200 shadow-2xl shadow-slate-900/20 flex flex-col overflow-hidden"
          style={{ animation: "slideUp 0.25s ease-out" }}
          data-testid="chat-bubble-panel">
          {/* Header */}
          <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-4 py-3 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2">
              {currentGroup && (
                <button onClick={() => { setCurrentGroup(null); setMessages([]); }} className="text-white/80 hover:text-white mr-1" data-testid="chat-back-btn">
                  <ChevronLeft className="w-5 h-5" />
                </button>
              )}
              <MessageSquare className="w-5 h-5 text-white" />
              <span className="font-semibold text-white text-sm">{currentGroup ? currentGroup.name : "Chat"}</span>
              {currentGroup && <span className="text-emerald-200 text-[10px]">{currentGroup.members?.length || 0} miembros</span>}
            </div>
            <button onClick={() => { setOpen(false); setCurrentGroup(null); }} className="text-white/70 hover:text-white" data-testid="chat-close-btn">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* CONTENT */}
          {showNewGroup ? (
            /* NEW GROUP FORM */
            <div className="flex-1 p-4 overflow-y-auto">
              <h3 className="font-semibold text-sm text-slate-900 mb-3">Nuevo Grupo</h3>
              <input value={newGroupName} onChange={e => setNewGroupName(e.target.value)} placeholder="Nombre del grupo..."
                className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                data-testid="bubble-group-name" />
              <p className="text-xs text-slate-500 mb-2">Seleccionar miembros:</p>
              <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-lg">
                {chatUsers.map(u => (
                  <label key={u.user_id} className="flex items-center gap-2 p-2 hover:bg-slate-50 cursor-pointer text-xs border-b border-slate-50 last:border-0">
                    <input type="checkbox" checked={newGroupMembers.includes(u.user_id)}
                      onChange={e => { if (e.target.checked) setNewGroupMembers(prev => [...prev, u.user_id]); else setNewGroupMembers(prev => prev.filter(m => m !== u.user_id)); }}
                      className="w-3.5 h-3.5 rounded" />
                    <span className="font-medium text-slate-700">{u.name || u.email}</span>
                    <span className="text-slate-400 ml-auto">{u.role}</span>
                  </label>
                ))}
              </div>
              <div className="flex gap-2 mt-3">
                <button onClick={() => setShowNewGroup(false)} className="flex-1 px-3 py-2 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50">Cancelar</button>
                <button onClick={handleCreateGroup} className="flex-1 px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700" data-testid="bubble-save-group">Crear</button>
              </div>
            </div>
          ) : !currentGroup ? (
            /* GROUP LIST */
            <div className="flex-1 overflow-y-auto" data-testid="chat-bubble-groups">
              {internalNotifications.length > 0 && <div className="p-3 border-b border-slate-100 bg-slate-50/80"><p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Canal formal</p><div className="space-y-2">{internalNotifications.slice(0, 4).map(item => <button key={item.notification_id} onClick={() => markNotificationRead(item.notification_id)} className={`w-full text-left p-2 rounded-lg border ${item.read ? 'bg-white border-slate-200' : 'bg-emerald-50 border-emerald-200'}`} data-testid={`bubble-notification-${item.notification_id}`}><p className="text-xs font-semibold text-slate-900">{item.title}</p><p className="text-[11px] text-slate-500 line-clamp-2">{item.body}</p></button>)}</div></div>}
              {isAdmin && (
                <button onClick={() => { setShowNewGroup(true); loadUsers(); }}
                  className="w-full flex items-center gap-2 p-3 text-sm text-emerald-600 hover:bg-emerald-50 border-b border-slate-100 font-medium"
                  data-testid="bubble-new-group-btn">
                  <Plus className="w-4 h-4" />Crear grupo
                </button>
              )}
              {groups.length === 0 ? (
                <div className="text-center py-12 px-4 text-slate-400">
                  <Hash className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                  <p className="text-sm font-medium">Sin grupos de chat</p>
                  {isAdmin && <p className="text-xs mt-1">Crea el primer grupo arriba</p>}
                </div>
              ) : groups.map(g => (
                <button key={g.group_id} onClick={() => setCurrentGroup(g)}
                  className="w-full flex items-center gap-3 p-3 hover:bg-slate-50 border-b border-slate-100 text-left transition-colors"
                  data-testid={`bubble-group-${g.group_id}`}>
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center text-white text-sm font-bold shrink-0">
                    {g.name?.charAt(0)?.toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-slate-900 truncate">{g.name}</p>
                    <p className="text-[11px] text-slate-400 truncate">{g.last_message || "Sin mensajes"}</p>
                  </div>
                  {g.last_message_at && (
                    <span className="text-[10px] text-slate-400 shrink-0">
                      {new Date(g.last_message_at).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" })}
                    </span>
                  )}
                </button>
              ))}
            </div>
          ) : (
            /* MESSAGES */
            <>
              <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2 bg-slate-50/60" data-testid="bubble-messages">
                {messages.length === 0 && (
                  <div className="text-center py-10 text-slate-400 text-xs">Sin mensajes. Envía el primero.</div>
                )}
                {messages.map(msg => {
                  const isMe = msg.sender_id === user?.user_id;
                  return (
                    <div key={msg.message_id} className={`flex ${isMe ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[75%] rounded-2xl px-3.5 py-2 ${isMe ? "bg-emerald-600 text-white rounded-br-sm" : "bg-white border border-slate-200 text-slate-800 rounded-bl-sm shadow-sm"}`}>
                        {!isMe && <p className="text-[10px] font-semibold mb-0.5 text-emerald-600">{msg.sender_name}</p>}
                        {msg.text && <p className="text-[13px] leading-relaxed">{msg.text}</p>}
                        {msg.file_name && (
                          <button onClick={() => handleDownload(msg.message_id, msg.file_name)}
                            className={`mt-1 flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-md ${isMe ? "bg-emerald-700/50 hover:bg-emerald-700/70 text-white" : "bg-slate-100 hover:bg-slate-200 text-slate-700"} transition-colors`}>
                            {msg.file_data ? <><Download className="w-3 h-3" />{msg.file_name}</> : <><FileText className="w-3 h-3 opacity-50" /><span className="line-through opacity-50">{msg.file_name}</span></>}
                          </button>
                        )}
                        <p className={`text-[9px] mt-0.5 ${isMe ? "text-emerald-200" : "text-slate-400"}`}>
                          {msg.created_at ? new Date(msg.created_at).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" }) : ""}
                        </p>
                      </div>
                    </div>
                  );
                })}
                <div ref={msgEndRef} />
              </div>
              {/* Input */}
              <div className="px-3 py-2.5 border-t border-slate-200 bg-white shrink-0">
                {chatFile && (
                  <div className="flex items-center gap-2 mb-1.5 px-2.5 py-1 rounded-md bg-violet-50 text-[11px] text-violet-700">
                    <Paperclip className="w-3 h-3" /><span className="truncate flex-1">{chatFile.name}</span>
                    <button onClick={() => setChatFile(null)}><X className="w-3 h-3" /></button>
                  </div>
                )}
                <div className="flex gap-1.5">
                  <label className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer shrink-0" data-testid="bubble-attach">
                    <Paperclip className="w-4 h-4 text-slate-400" />
                    <input type="file" className="hidden" onChange={e => setChatFile(e.target.files?.[0] || null)} />
                  </label>
                  <input value={chatInput} onChange={e => setChatInput(e.target.value)} placeholder="Mensaje..."
                    className="flex-1 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    onKeyDown={e => e.key === "Enter" && handleSend()} data-testid="bubble-chat-input" />
                  <button onClick={handleSend} className="p-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white shrink-0 transition-colors" data-testid="bubble-send-btn">
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px) scale(0.95); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </>
  );
};

export default ChatBubble;
